import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { 
  Download, Smartphone, Star, Zap, Shield, AlertCircle, 
  Share, PlusSquare, ArrowUpFromLine, MoreVertical, Home,
  CheckCircle2, Apple, SmartphoneIcon, Monitor
} from 'lucide-react'

function InstallPrompt() {
  const { language } = useLanguage()
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [platform, setPlatform] = useState('android')
  const [installing, setInstalling] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [showManual, setShowManual] = useState(false)

  useEffect(() => {
    // Detect platform
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
    const isAndroid = /Android/.test(navigator.userAgent)
    const isWindows = /Win/.test(navigator.platform)

    if (isIOS) {
      setPlatform('ios')
    } else if (isAndroid) {
      setPlatform('android')
    } else if (isWindows) {
      setPlatform('windows')
    } else {
      setPlatform('desktop')
    }

    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || navigator.standalone === true
      || document.referrer.includes('android-app://') === true

    setIsInstalled(isStandalone)

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
    }

    // Listen for successful install
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true)
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
        setDeferredPrompt(null)
      } else {
        setInstalling(false)
      }
    } else {
      // Show manual instructions for Windows when deferredPrompt is null
      setShowManual(true)
    }
  }

  const isRTL = language === 'ar'

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: 'var(--bg-primary)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <motion.div
        className="w-full max-w-md"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {/* Platform Icon */}
        <motion.div
          className="flex justify-center mb-6"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
        >
          {platform === 'ios' ? (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center border border-gray-600">
              <Apple className="w-10 h-10 text-white" />
            </div>
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
              <SmartphoneIcon className="w-10 h-10 text-white" />
            </div>
          )}
        </motion.div>

        {/* App Logo */}
        <motion.div
          className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl flex items-center justify-center shadow-2xl shadow-yellow-400/25"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: 'spring' }}
        >
          <Zap className="w-12 h-12 text-black" />
        </motion.div>

        {/* Title */}
        <motion.h1 
          className="text-3xl font-black text-center text-white mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Oxygen Gym
        </motion.h1>

        <motion.p 
          className="text-gray-400 text-center mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {language === 'ar' ? 'تطبيق احترافي للياقة البدنية' : 'Professional Fitness App'}
        </motion.p>

        {/* Features */}
        <motion.div 
          className="flex justify-center gap-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Zap className="w-4 h-4 text-yellow-400" />
            <span>{language === 'ar' ? 'سريع' : 'Fast'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Shield className="w-4 h-4 text-yellow-400" />
            <span>{language === 'ar' ? 'آمن' : 'Secure'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Smartphone className="w-4 h-4 text-yellow-400" />
            <span>{language === 'ar' ? 'بدون إنترنت' : 'Offline'}</span>
          </div>
        </motion.div>

        {/* Platform Specific Instructions */}
        {platform === 'ios' ? (
          <IOSInstructions language={language} isRTL={isRTL} />
        ) : platform === 'windows' ? (
          <WindowsInstructions
            language={language}
            isRTL={isRTL}
            deferredPrompt={deferredPrompt}
            installing={installing}
            onInstall={handleInstall}
            isInstalled={isInstalled}
            showManual={showManual}
          />
        ) : (
          <AndroidInstructions
            language={language}
            isRTL={isRTL}
            deferredPrompt={deferredPrompt}
            installing={installing}
            onInstall={handleInstall}
            isInstalled={isInstalled}
          />
        )}

        {/* Footer */}
        <motion.div 
          className="mt-8 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="flex items-center justify-center gap-2 text-yellow-400 mb-2">
            <Star className="w-4 h-4 fill-yellow-400" />
            <Star className="w-4 h-4 fill-yellow-400" />
            <Star className="w-4 h-4 fill-yellow-400" />
            <Star className="w-4 h-4 fill-yellow-400" />
            <Star className="w-4 h-4 fill-yellow-400" />
          </div>
          <p className="text-sm text-gray-500">
            {language === 'ar' ? 'مجاني 100% • بدون إعلانات' : '100% Free • No Ads'}
          </p>
        </motion.div>
      </motion.div>
    </motion.div>
  )
}

// iOS Instructions Component
function IOSInstructions({ language, isRTL }) {
  const steps = [
    {
      icon: Share,
      text: language === 'ar' ? 'اضغط على زر المشاركة' : 'Tap on Share button',
      subtext: language === 'ar' ? 'في أسفل الشاشة' : 'at the bottom of the screen',
      color: 'text-blue-400'
    },
    {
      icon: PlusSquare,
      text: language === 'ar' ? 'اختر "إضافة إلى الشاشة الرئيسية"' : 'Select "Add to Home Screen"',
      subtext: language === 'ar' ? 'من قائمة الخيارات' : 'from the options menu',
      color: 'text-green-400'
    },
    {
      icon: CheckCircle2,
      text: language === 'ar' ? 'اضغط "إضافة"' : 'Tap "Add"',
      subtext: language === 'ar' ? 'في الأعلى' : 'at the top',
      color: 'text-yellow-400'
    }
  ]

  return (
    <motion.div
      className="glass-effect rounded-3xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
    >
      {/* Header */}
      <div className={`flex items-center gap-3 mb-6 pb-4 border-b border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <Apple className="w-6 h-6 text-blue-400" />
        </div>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h3 className="text-white font-bold text-lg">
            {language === 'ar' ? 'تثبيت على iPhone / iPad' : 'Install on iPhone / iPad'}
          </h3>
          <p className="text-gray-400 text-sm">
            {language === 'ar' ? 'اتبع الخطوات التالية' : 'Follow these steps'}
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 + (index * 0.1) }}
          >
            {/* Step Number */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold text-lg ${isRTL ? 'order-2' : ''}`}>
              {index + 1}
            </div>
            
            {/* Icon */}
            <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
              <step.icon className={`w-6 h-6 ${step.color}`} />
            </div>
            
            {/* Text */}
            <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
              <p className="text-white font-medium">{step.text}</p>
              <p className="text-gray-500 text-sm">{step.subtext}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Visual Guide */}
      <motion.div
        className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
      >
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Home className="w-5 h-5 text-blue-400" />
          </div>
          <p className={`text-sm text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>
            {language === 'ar' 
              ? 'سيتم إضافة التطبيق إلى شاشتك الرئيسية للوصول السريع'
              : 'The app will be added to your home screen for quick access'}
          </p>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Android Instructions Component
function AndroidInstructions({ language, isRTL, deferredPrompt, installing, onInstall, isInstalled }) {
  return (
    <motion.div
      className="glass-effect rounded-3xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
    >
      {/* Header */}
      <div className={`flex items-center gap-3 mb-6 pb-4 border-b border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
          <SmartphoneIcon className="w-6 h-6 text-green-400" />
        </div>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h3 className="text-white font-bold text-lg">
            {isInstalled
              ? (language === 'ar' ? 'تم التثبيت مسبقاً' : 'Already Installed')
              : (language === 'ar' ? 'تثبيت على Android' : 'Install on Android')}
          </h3>
          <p className="text-gray-400 text-sm">
            {language === 'ar' ? 'اضغط الزر أدناه للتثبيت' : 'Click the button below to install'}
          </p>
        </div>
      </div>

      {/* Installed State */}
      {isInstalled && (
        <motion.div
          className="mb-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/20"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
            <div className={isRTL ? 'text-right' : 'text-left'}>
              <p className="text-white font-bold">
                {language === 'ar' ? 'التطبيق مثبت!' : 'App is Installed!'}
              </p>
              <p className="text-gray-400 text-sm">
                {language === 'ar'
                  ? 'ابحث عن Oxygen Gym على شاشتك الرئيسية'
                  : 'Look for Oxygen Gym on your home screen'}
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {deferredPrompt ? (
        // Can install directly
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.8 }}
        >
          <button
            onClick={onInstall}
            disabled={installing}
            className="w-full py-5 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-2xl hover:shadow-lg hover:shadow-green-500/25 transition-all duration-300 flex items-center justify-center gap-3 text-lg"
          >
            {installing ? (
              <>
                <motion.div
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                {language === 'ar' ? 'جاري التثبيت...' : 'Installing...'}
              </>
            ) : (
              <>
                <Download className="w-6 h-6" />
                {language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
              </>
            )}
          </button>

          <p className={`mt-4 text-sm text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
            {language === 'ar'
              ? 'سيظهر التطبيق على شاشتك الرئيسية بعد التثبيت'
              : 'The app will appear on your home screen after installation'}
          </p>
        </motion.div>
      ) : (
        // Manual instructions needed
        <div className="space-y-4">
          <motion.div
            className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
              1
            </div>
            <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
              <MoreVertical className="w-6 h-6 text-green-400" />
            </div>
            <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
              <p className="text-white font-medium">
                {language === 'ar'
                  ? 'اضغط على قائمة المتصفح (ثلاث نقاط)'
                  : 'Tap on browser menu (three dots)'}
              </p>
              <p className="text-gray-500 text-sm">
                {language === 'ar' ? 'في أعلى الزاوية' : 'In the top corner'}
              </p>
            </div>
          </motion.div>

          <motion.div
            className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
              2
            </div>
            <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
              <ArrowUpFromLine className="w-6 h-6 text-green-400" />
            </div>
            <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
              <p className="text-white font-medium">
                {language === 'ar'
                  ? 'اختر "تثبيت التطبيق" أو "إضافة إلى الشاشة الرئيسية"'
                  : 'Select "Install App" or "Add to Home Screen"'}
              </p>
              <p className="text-gray-500 text-sm">
                {language === 'ar' ? 'من القائمة المنسدلة' : 'From the dropdown menu'}
              </p>
            </div>
          </motion.div>

          <motion.div
            className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0 }}
          >
            <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
              3
            </div>
            <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
              <CheckCircle2 className="w-6 h-6 text-green-400" />
            </div>
            <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
              <p className="text-white font-medium">
                {language === 'ar' ? 'اضغط "تثبت" في النافذة' : 'Tap "Install" in the popup'}
              </p>
              <p className="text-gray-500 text-sm">
                {language === 'ar' ? 'لإكمال التثبيت' : 'To complete installation'}
              </p>
            </div>
          </motion.div>

          {/* Reinstall hint */}
          {isInstalled && (
            <motion.div
              className={`mt-4 p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 ${isRTL ? 'text-right' : ''}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
            >
              <p className="text-yellow-400 text-sm">
                {language === 'ar'
                  ? '💡 لإعادة التثبيت: احذف التطبيق من إعدادات الهاتف أولاً'
                  : '💡 To reinstall: Remove the app from phone settings first'}
              </p>
            </motion.div>
          )}
        </div>
      )}

      {/* Visual Guide */}
      <motion.div
        className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
      >
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <Home className="w-5 h-5 text-green-400" />
          </div>
          <p className={`text-sm text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>
            {language === 'ar'
              ? 'سيتم إضافة التطبيق إلى شاشتك الرئيسية للوصول السريع'
              : 'The app will be added to your home screen for quick access'}
          </p>
        </div>
      </motion.div>
    </motion.div>
  )
}

// Windows Instructions Component
function WindowsInstructions({ language, isRTL, deferredPrompt, installing, onInstall, isInstalled, showManual }) {
  return (
    <motion.div
      className="glass-effect rounded-3xl p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.7 }}
    >
      {/* Header */}
      <div className={`flex items-center gap-3 mb-6 pb-4 border-b border-white/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
          <Monitor className="w-6 h-6 text-blue-400" />
        </div>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h3 className="text-white font-bold text-lg">
            {language === 'ar' ? 'تثبيت على Windows' : 'Install on Windows'}
          </h3>
          <p className="text-gray-400 text-sm">
            {language === 'ar' ? 'استخدم Chrome أو Edge' : 'Use Chrome or Edge browser'}
          </p>
        </div>
      </div>

      {/* ALWAYS SHOW INSTALL BUTTON FOR WINDOWS */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
      >
        <button
          onClick={onInstall}
          disabled={installing}
          className="w-full py-5 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-2xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 flex items-center justify-center gap-3 text-lg"
        >
          {installing ? (
            <>
              <motion.div
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              {language === 'ar' ? 'جاري التثبيت...' : 'Installing...'}
            </>
          ) : (
            <>
              <Download className="w-6 h-6" />
              {language === 'ar' ? 'تثبيت التطبيق' : 'Install App'}
            </>
          )}
        </button>

        <p className={`mt-4 text-sm text-gray-400 ${isRTL ? 'text-right' : 'text-left'}`}>
          {language === 'ar'
            ? '💡 انقر فوق الزر ثم اتبع الخطوات أدناه'
            : '💡 Click the button then follow the steps below'}
        </p>
      </motion.div>

      {/* Show Manual Instructions Alert if button was clicked */}
      {showManual && !deferredPrompt && (
        <motion.div
          className="mt-4 p-4 rounded-xl bg-yellow-500/20 border border-yellow-500/30"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <p className={`text-yellow-400 font-bold ${isRTL ? 'text-right' : 'text-left'}`}>
            {language === 'ar'
              ? '⚠️ إذا لم يظهر زر التثبيت، اتبع الخطوات التالية:'
              : '⚠️ If install dialog doesn\'t appear, follow these steps:'}
          </p>
        </motion.div>
      )}

      {/* Manual Instructions - Always Visible */}
      <div className="mt-6 space-y-4">
        <motion.div
          className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
          initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
        >
          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
            1
          </div>
          <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
            <MoreVertical className="w-6 h-6 text-blue-400" />
          </div>
          <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
            <p className="text-white font-medium">
              {language === 'ar'
                ? 'انقر على ⋮ (قائمة المتصفح) في أعلى يمين الصفحة'
                : 'Click ⋮ (browser menu) in the top right corner'}
            </p>
          </div>
        </motion.div>

        <motion.div
          className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
          initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.0 }}
        >
          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
            2
          </div>
          <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
            <ArrowUpFromLine className="w-6 h-6 text-blue-400" />
          </div>
          <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
            <p className="text-white font-medium">
              {language === 'ar'
                ? 'اختر "تثبيت التطبيق" من القائمة'
                : 'Select "Install App" from the menu'}
            </p>
          </div>
        </motion.div>

        <motion.div
          className={`flex items-start gap-4 p-4 rounded-2xl bg-white/5 ${isRTL ? 'flex-row-reverse text-right' : ''}`}
          initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.1 }}
        >
          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-yellow-400 font-bold ${isRTL ? 'order-2' : ''}`}>
            3
          </div>
          <div className={`flex-shrink-0 ${isRTL ? 'order-1' : ''}`}>
            <CheckCircle2 className="w-6 h-6 text-blue-400" />
          </div>
          <div className={`flex-1 ${isRTL ? 'order-3' : ''}`}>
            <p className="text-white font-medium">
              {language === 'ar'
                ? 'انقر "تثبيت" في النافذة المنبثقة'
                : 'Click "Install" in the popup dialog'}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Visual Guide */}
      <motion.div
        className="mt-6 p-4 rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
      >
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Monitor className="w-5 h-5 text-blue-400" />
          </div>
          <p className={`text-sm text-gray-300 ${isRTL ? 'text-right' : 'text-left'}`}>
            {language === 'ar'
              ? '✓ سيتم إضافة التطبيق إلى سطح مكتبك'
              : '✓ The app will be added to your desktop'}
          </p>
        </div>
      </motion.div>

      {/* Installed State Badge */}
      {isInstalled && (
        <motion.div
          className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center gap-3"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3 }}
        >
          <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
          <p className="text-green-400 text-sm">
            {language === 'ar' ? '✓ التطبيق مثبت بالفعل' : '✓ App is already installed'}
          </p>
        </motion.div>
      )}
    </motion.div>
  )
}

export default InstallPrompt